/**
 * @ngdoc type
 * @module flowingly.signup
 * @name signupController
 *
 * @description
 */
import * as angular from 'angular';
import IInsertBusinessCommand from '@Shared.Angular/@types/core/contracts/commands/business/insertBusinessCommand';
import IBusinessDetail from '@Shared.Angular/@types/core/contracts/queryModel/business/businessDetail';
import ISettingDetail from '@Shared.Angular/@types/core/contracts/config/ISettingDetail';
import SignupService from './signup.service';
import { IStateService } from 'angular-ui-router';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IBusinessTypeDetail from '@Shared.Angular/@types/core/contracts/queryModel/business/businessTypeDetail';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

class SignupController {
  static $inject = [
    'signupService',
    '$state',
    'sessionService',
    'tokenService',
    'authService',
    'validationService',
    'fileService',
    'permissionsService',
    'flowinglyConstants',
    'businessApiService'
  ];
  public emailValidationPattern;
  public businessNameValidationPattern = /a-z/i;
  public errorOnSubmit = false;
  public errorMessagesOnSubmit = '';
  public signup = {
    userFirstName: 'Flowingly',
    userLastName: 'Support'
  } as IInsertBusinessCommand;
  public templates = [];
  public businesses: IBusinessDetail[];
  public featureSettings: ISettingDetail[];
  public selectedSettings: { name: string; value: boolean }[] = [];
  public selectedFeatureSet = '';
  public brandingSettings: ISettingDetail[];
  public timeZones: string[];
  public industries;
  public signupForm;
  public loading = true;
  public logoUploaded = (fileId: string) => {
    if (this.signup.logoId) {
      this.fileService.removeFileForId(this.signup.logoId);
    }
    this.signup.logoId = fileId;
  };
  public businessTypes: IBusinessTypeDetail[];

  constructor(
    private signupService: SignupService,
    private $state: IStateService,
    private sessionService: SharedAngular.SessionService,
    private tokenService: SharedAngular.TokenService,
    private authService: AuthService,
    private validationService: SharedAngular.ValidationService,
    private fileService: SharedAngular.FileService,
    private permissionsService: SharedAngular.PermissionsService,
    private flowinglyConstants: SharedAngular.FlowinglyConstants,
    private businessApiService: BusinessApiService
  ) {
    if (
      !this.permissionsService.currentUserHasPermission(
        this.flowinglyConstants.permissions.BUSINESS_SIGNUP
      )
    ) {
      $state.go(STATE_MAPPINGS.flowsactive);
      return;
    }

    this.emailValidationPattern =
      this.validationService.getEmailValidationPattern();
    this.industries = signupService.industries;

    const templatePromise = this.getTemplateBusinesses();
    const businessPromise = this.getParentBusinesses();
    const timeZonePromise = this.getTimeZones();
    const businessTypesPromise = this.getBusinessTypes();

    const loadingPromises = [
      templatePromise,
      businessPromise,
      timeZonePromise,
      businessTypesPromise
    ];
    Promise.all(loadingPromises).then(() => (this.loading = false));
  }

  public signupBusiness() {
    if (this.signupForm.$valid) {
      this.errorOnSubmit = false;
      this.errorMessagesOnSubmit = '';
      this.submitNewBusiness();
    }
  }

  private submitNewBusiness() {
    this.loading = true;
    const featureSettings = structuredClone(this.featureSettings);
    for (const setting of this.selectedSettings) {
      const featureSetting = featureSettings.find(
        (fs) => fs.name === setting.name
      );
      if (featureSetting) {
        featureSetting.value = setting.value.toString();
      }
    }
    this.signup.settings = featureSettings.concat(
      this.brandingSettings.filter((setting) => setting.copy)
    );
    const signupDetails = angular.copy(this.signup);
    const businessType = this.businessTypes.find(
      (bt) => bt.id === this.signup.businessTypeId
    );
    if (businessType.name !== 'Live') {
      signupDetails.displayName = `${signupDetails.displayName} (${businessType.name})`;
    }
    signupDetails.name = null;
    this.signupService.signupBusiness(signupDetails).then(
      async (result) => {
        await this.saveUserAndToken(result);
        this.loading = false;
        this.sessionService.setuserAuthenticated();
        $('.changed-input').removeClass('changed-input');
        this.$state.go(STATE_MAPPINGS.login);
      },
      (response) => {
        this.signupForm.$invalid = true;
        this.errorOnSubmit = true;
        this.errorMessagesOnSubmit = response.data?.message;
        this.loading = false;
      }
    );
  }

  private async saveUserAndToken(result) {
    await this.authService.setUserAfterSuccessfulLogin(result.data.user);
    this.tokenService.setToken(result.data.token);
  }

  private getTemplateBusinesses() {
    return this.signupService.getTemplateBusinesses().then((templates) => {
      this.templates = templates.map((template) => {
        return {
          name: template.name,
          id: template.id
        };
      });
    });
  }

  private getParentBusinesses() {
    return this.signupService.getExistingBusinesses().then((businesses) => {
      this.signup.parentId = businesses.find((b) => b.name === 'Flowingly').id;
      this.businesses = businesses;
      this.getParentSettings(this.signup.parentId);
    });
  }

  private getTimeZones() {
    return this.businessApiService.getTimeZones().then((timeZones) => {
      this.timeZones = timeZones.sort();
      this.signup.timeZone = timeZones.find(
        (tz) => tz.indexOf('New Zealand') > -1
      );
    });
  }

  private getBusinessTypes() {
    return this.businessApiService
      .getTypes()
      .then((types) => (this.businessTypes = types));
  }

  public getParentSettings(parentId: string) {
    this.signupService.getSignupSettings(parentId).then((settings) => {
      this.featureSettings = settings
        .filter((setting) =>
          setting.tags.some((tag) => tag.name === 'signup_feature')
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      this.brandingSettings = settings
        .filter((setting) =>
          setting.tags.some((tag) => tag.name === 'signup_branding')
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      this.brandingSettings.forEach((setting) => (setting.copy = true));
    });
  }

  public getTypeDescription(id: number) {
    return this.businessTypes?.find((bt) => bt.id === id)?.description;
  }

  public onFeatureSetChange() {
    if (this.selectedFeatureSet) {
      const enabledTag = this.selectedFeatureSet + '_enabled';
      this.selectedSettings = this.featureSettings
        .filter((setting) =>
          setting.tags.some((tag) =>
            tag.name.startsWith(this.selectedFeatureSet)
          )
        )
        .map((setting) => {
          return {
            name: setting.name,
            value: setting.tags.some((tag) => tag.name === enabledTag)
          };
        });
    } else {
      this.selectedSettings = [];
    }
  }
}

angular
  .module('flowingly.signup')
  .controller('signupController', SignupController);
